import React, { useEffect } from 'react';
import { User, Mail, Phone, Calendar, Briefcase, FileText, Newspaper, UserCheck, Scale, Tag } from 'lucide-react';
import { MultiChipRow, PropTextRow, PropToggleRow } from '../shared/property-rows/rows';
import { supabase } from '../../lib/supabase';
import { useClientTagStore } from '../../lib/stores/clientTagStore';

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

/** Shape del draft referente condivisa tra drawer creazione e sezione Persone. */
export const EMPTY_REFERENTE_DRAFT = {
  first_name: '', last_name: '', email: '', phone: '', birth_date: '',
  role: '', notes: '', newsletter_subscribed: false, is_referente: true,
  is_rappresentante_legale: false,
};

interface ReferenteInlineFormProps {
  title: string;
  draft: Record<string, any>;
  onField: (key: string) => (value: any) => void;
  onCancel: () => void;
  onSave: () => void;
  saveLabel: string;
  saving?: boolean;
}

/**
 * Form inline a righe di proprietà per aggiungere/modificare un referente.
 * Usato da ReferentiRows (drawer anagrafica) e LeadPeopleSection (pannello).
 */
export function ReferenteInlineForm({ title, draft, onField, onCancel, onSave, saveLabel, saving }: ReferenteInlineFormProps) {
  const { tags, fetchTags } = useClientTagStore();
  useEffect(() => { fetchTags(); }, [fetchTags]);

  // Tag della persona: si caricano quelli gia' assegnati prima di poterli cambiare.
  // Finche' draft.tag_ids e' undefined il salvataggio non li tocca (persistClientReferenti).
  const personId: string | undefined = draft.person_id || draft.person_client_id;
  const tagsLoaded = Array.isArray(draft.tag_ids);
  useEffect(() => {
    if (tagsLoaded) return;
    if (!personId) { onField('tag_ids')([]); return; }
    let cancelled = false;
    supabase.from('client_tag_assignments').select('tag_id').eq('client_id', personId)
      .then(({ data, error }) => {
        if (cancelled || error) return;
        onField('tag_ids')((data || []).map((t: { tag_id: string }) => t.tag_id));
      });
    return () => { cancelled = true; };
    // onField cambia a ogni render del genitore: basta la persona
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId, tagsLoaded]);
  const selectedTags: string[] = draft.tag_ids || [];
  const tagOptions = tags.map((t) => ({ value: t.id, label: t.name, color: t.color }));

  return (
    <div className="mt-1 rounded-md border border-[rgba(55,53,47,0.09)] px-2 py-1.5 dark:border-white/10">
      <p className="mb-1 px-1.5 text-[11px] font-medium uppercase tracking-wide text-[rgba(55,53,47,0.5)] dark:text-gray-400">
        {title}
      </p>
      <PropTextRow icon={rowIcon(User)} label="Nome" value={draft.first_name} onChange={onField('first_name')} />
      <PropTextRow icon={rowIcon(User)} label="Cognome" value={draft.last_name} onChange={onField('last_name')} />
      <PropTextRow icon={rowIcon(Mail)} label="Email" type="email" value={draft.email} onChange={onField('email')} />
      <PropTextRow icon={rowIcon(Phone)} label="Telefono" value={draft.phone} onChange={onField('phone')} />
      <PropTextRow icon={rowIcon(Calendar)} label="Data di nascita" type="date" value={draft.birth_date} onChange={onField('birth_date')} placeholder="" />
      <PropTextRow icon={rowIcon(Briefcase)} label="Ruolo" value={draft.role} onChange={onField('role')} />
      <PropTextRow icon={rowIcon(FileText)} label="Note" value={draft.notes} onChange={onField('notes')} />
      <PropToggleRow icon={rowIcon(Newspaper)} label="Newsletter" checked={!!draft.newsletter_subscribed} onChange={onField('newsletter_subscribed')} onLabel="Iscrizione attiva" offLabel="Non iscritto" />
      {tagsLoaded && (
        <MultiChipRow
          icon={rowIcon(Tag)}
          label="Tag"
          selected={selectedTags}
          options={tagOptions}
          onAdd={(id) => onField('tag_ids')([...selectedTags, id])}
          onRemove={(id) => onField('tag_ids')(selectedTags.filter((x) => x !== id))}
          addPlaceholder="Aggiungi tag..."
        />
      )}
      <PropToggleRow icon={rowIcon(UserCheck)} label="Referente" checked={!!draft.is_referente} onChange={onField('is_referente')} onLabel="Sì" offLabel="No" />
      <PropToggleRow icon={rowIcon(Scale)} label="Rappr. legale" checked={!!draft.is_rappresentante_legale} onChange={onField('is_rappresentante_legale')} onLabel="Sì" offLabel="No" />
      <div className="mt-1 flex justify-end gap-2 px-1.5 pb-0.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded px-2 py-1 text-[12.5px] font-medium text-[#37352F] hover:bg-[rgba(55,53,47,0.08)] dark:text-white dark:hover:bg-white/[0.06]"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded bg-[#37352F] px-2.5 py-1 text-[12.5px] font-semibold text-white hover:opacity-90 disabled:opacity-50 dark:bg-white dark:text-[#191919]"
        >
          {saveLabel}
        </button>
      </div>
    </div>
  );
}
